import React, { useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, InfoIcon, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import MainWrapper from 'layouts/MainWrapper'
import { avatarUrl } from 'utils/avatarUrl'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useSession } from 'views/Community/components/useSession'
import { type UserProfile } from 'state/services/indexNode/indexNode'
import {
  useGetOwnProfileQuery,
  useUpdateOwnProfileMutation,
  useUploadOwnAvatarMutation,
  useRemoveOwnAvatarMutation,
  useGetVotingPowerQuery
} from 'state/services/community/community'
import useToast from 'hooks/useToast'
import isNil from 'lodash/isNil'

/*
 * The offsets live here, not on the element.
 *
 * This was `margin: 0 auto` with `mt="120px"` passed as a prop, and the
 * shorthand won: the page rendered flush against the top and the first line
 * sat under the sticky header. A `margin` shorthand resets every side,
 * including the one a prop had just set, and nothing warns you.
 *
 * 152px is the offset the DAO and Community pages use to clear the header and
 * the promo bar above it — the DAO view carries the note that 32px puts the
 * title under the logo.
 */
const Column = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 620px;
  margin: 152px auto 120px;
`

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const TextArea = styled(Input).attrs({ as: 'textarea' })`
  min-height: 90px;
  resize: vertical;
`

const SwitchRow = styled.button<{ $on: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.purple : theme.colors.gray8)};
  background: ${({ theme, $on }) => ($on ? `${theme.colors.purple}1a` : 'transparent')};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`

const Pill = styled.span<{ $on: boolean }>`
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme, $on }) => ($on ? theme.colors.purple : theme.colors.gray6)};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.purple : theme.colors.gray8)};
`

/**
 * My profile.
 *
 * The whole page is written around one property: **nothing here is visible to
 * anybody else until the switch at the bottom is on**. That is why the switch
 * is not a checkbox tucked beside a field — it is the largest control on the
 * page and it says, in a sentence, what it does.
 */
/*
 * The bar is full at 100 voting power. A fixed reference, not the largest
 * holder of the day: anything relative means your own bar moves when somebody
 * else buys, which is a lie about you told with somebody else's data.
 */
const POWER_SCALE = 100

const PowerRow = styled(Flex)`
  align-items: center;
  gap: 12px;
`

const PowerTrack = styled.div`
  flex: 1;
  height: 10px;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.gray9};
  overflow: hidden;
`

const PowerFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.cyan};
  transition: width 200ms ease;
`

const AvatarRow = styled(Flex)`
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
`

const AvatarPreview = styled.div<{ $src?: string }>`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme, $src }) =>
    $src ? `url(${$src}) center/cover no-repeat` : theme.colors.gray9};
`

const AvatarActions = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
`

/* The input itself is never shown: a file field cannot be styled, and the
   label is what people click. */
const HiddenFileInput = styled.input`
  display: none;
`

const AvatarButton = styled.label`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  font-size: 13px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
`

const AvatarLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.cyan};
`

const Profile: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { account } = useWeb3React()
  const { toastError, toastSuccess } = useToast()
  /*
   * The same tolerant lookup the rest of the community uses. This line was
   * `lifetimeTokens?.[account.toLowerCase()]`, and the map is keyed by the
   * checksummed address the wallet reports — so a signed-in person had no
   * token as far as this page was concerned.
   */
  const { accessToken } = useSession()

  const { data, isLoading } = useGetOwnProfileQuery(
    { account: account as string, accessToken: accessToken as string },
    { skip: !account || isNil(accessToken) }
  )
  const [updateProfile, { isLoading: saving }] = useUpdateOwnProfileMutation()
  const { data: power } = useGetVotingPowerQuery(
    { wallet: account as string },
    { skip: !account }
  )
  const [uploadAvatar, { isLoading: uploadingAvatar }] = useUploadOwnAvatarMutation()
  const [removeAvatar] = useRemoveOwnAvatarMutation()

  /*
   * The picture is saved on choosing it, not on Save.
   *
   * It is a separate request to a separate service — batching it with the form
   * would mean a Save that half-succeeds, with no way to say which half. The
   * size is checked here as well as in storage: refused here it costs one
   * click, refused there it costs a round trip through two services first.
   */
  const onPickAvatar = async (file: File | undefined) => {
    if (!file || !account || !accessToken) return
    if (file.size > 2 * 1024 * 1024) {
      toastError(t('profile.avatarTooLargeTitle'), t('profile.avatarTooLarge'))
      return
    }
    try {
      await uploadAvatar({ account, accessToken, file }).unwrap()
      toastSuccess(t('profile.avatarSaved'))
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
      toastError(t('profile.avatarFailed'), message ?? t('profile.avatarFailedHint'))
    }
  }

  const [form, setForm] = useState<Partial<UserProfile>>({})
  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const set = (k: keyof UserProfile) => (v: string) => setForm((prev) => ({ ...prev, [k]: v }))

  /**
   * The same rule the server enforces, said before the request rather than
   * after it. The server remains the authority — this only spares somebody a
   * round trip to be told their name has a full stop in it.
   */
  const tagProblem = useMemo(() => {
    const tag = (form.tag ?? '').trim()
    if (!tag) return null
    return /^[a-z0-9_]{3,30}$/.test(tag) ? null : t('profile.tagRules')
  }, [form.tag, t])

  const onSave = async () => {
    if (!account || !accessToken) return
    try {
      await updateProfile({
        account,
        accessToken,
        patch: {
          name: form.name ?? '',
          tag: (form.tag ?? '').trim() || undefined,
          bio: form.bio ?? '',
          linkX: form.linkX ?? '',
          linkLinkedIn: form.linkLinkedIn ?? '',
          linkFacebook: form.linkFacebook ?? '',
          isPublic: Boolean(form.isPublic)
        }
      }).unwrap()
      toastSuccess(t('profile.saved'))
    } catch (e) {
      // The server's message is the useful one here: "that name is taken" and
      // "that name is reserved" are both things only it can know.
      const message = (e as { data?: { message?: string } })?.data?.message
      toastError(message || t('profile.saveFailed'))
    }
  }

  if (!account) {
    return (
      <MainWrapper>
        <Column>
          <Text variant={TextVariant.BODY_DEFAULT}>{t('profile.connectFirst')}</Text>
        </Column>
      </MainWrapper>
    )
  }

  return (
    <MainWrapper>
      <Column>
        <Text variant={TextVariant.H3}>{t('profile.title')}</Text>

        {/*
          The power bar the brief asked for, and the number beside it.
          The bar is filled against a fixed reference rather than against the
          largest holder: a bar that rescales as somebody else buys makes your
          own standing appear to fall when nothing about you changed.
        */}
        <Section>
          <Text variant={TextVariant.BODY_SMALL_BOLD}>
            {t('profile.power')}
            <InfoIcon
              iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
              tooltipProps={{ title: t('profile.powerTooltip') }}
            />
          </Text>
          <PowerRow>
            <PowerTrack>
              <PowerFill
                $pct={
                  power?.votingPower == null
                    ? 0
                    : Math.min(100, Math.round((power.votingPower / POWER_SCALE) * 100))
                }
              />
            </PowerTrack>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
              {power?.votingPower == null ? '—' : power.votingPower.toLocaleString()}
            </Text>
          </PowerRow>
          {power?.votingPower == null ? (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('profile.powerUnknown')}
            </Text>
          ) : null}
        </Section>

        <Section>
          <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('profile.avatar')}</Text>
          <AvatarRow>
            <AvatarPreview $src={avatarUrl(data?.avatarPath)} />
            <AvatarActions>
              <HiddenFileInput
                id="avatar-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => void onPickAvatar(e.target.files?.[0])}
              />
              <AvatarButton htmlFor="avatar-input">
                {uploadingAvatar ? t('profile.avatarUploading') : t('profile.avatarChoose')}
              </AvatarButton>
              {data?.avatarPath ? (
                <AvatarLink
                  type="button"
                  onClick={() => {
                    if (account && accessToken) void removeAvatar({ account, accessToken })
                  }}
                >
                  {t('profile.avatarRemove')}
                </AvatarLink>
              ) : null}
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {t('profile.avatarHint')}
              </Text>
            </AvatarActions>
          </AvatarRow>

          <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('profile.name')}</Text>
          <Input
            value={form.name ?? ''}
            maxLength={60}
            onChange={(e) => set('name')(e.target.value)}
            placeholder={t('profile.namePlaceholder')}
          />
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            {t('profile.nameHint')}
          </Text>

          <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('profile.tag')}</Text>
          <Input
            value={form.tag ?? ''}
            maxLength={30}
            spellCheck={false}
            onChange={(e) => set('tag')(e.target.value.toLowerCase())}
            placeholder="node_runner"
          />
          {tagProblem ? (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.red}>
              {tagProblem}
            </Text>
          ) : (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('profile.tagHint')}
            </Text>
          )}

          <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('profile.bio')}</Text>
          <TextArea value={form.bio ?? ''} maxLength={500} onChange={(e) => set('bio')(e.target.value)} />
        </Section>

        <Section>
          <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('profile.links')}</Text>
          <Input value={form.linkX ?? ''} onChange={(e) => set('linkX')(e.target.value)} placeholder="X" />
          <Input
            value={form.linkLinkedIn ?? ''}
            onChange={(e) => set('linkLinkedIn')(e.target.value)}
            placeholder="LinkedIn"
          />
          <Input
            value={form.linkFacebook ?? ''}
            onChange={(e) => set('linkFacebook')(e.target.value)}
            placeholder="Facebook"
          />
        </Section>

        <SwitchRow
          type="button"
          $on={Boolean(form.isPublic)}
          onClick={() => setForm((p) => ({ ...p, isPublic: !p.isPublic }))}
        >
          <Flex flexDirection="column" gap="4px">
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('profile.visibilityTitle')}</Text>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {form.isPublic ? t('profile.visibilityOn') : t('profile.visibilityOff')}
            </Text>
          </Flex>
          <Pill $on={Boolean(form.isPublic)}>{form.isPublic ? t('profile.public') : t('profile.private')}</Pill>
        </SwitchRow>

        <Button
          variant="primary"
          size="md"
          disabled={isLoading || saving || Boolean(tagProblem)}
          onClick={() => void onSave()}
        >
          {saving ? t('profile.saving') : t('profile.save')}
        </Button>
      </Column>
    </MainWrapper>
  )
}

export default Profile
